import { SEOHead } from '@/components/seo/SEOHead';
import { useSanityPage } from '@/lib/sanity.hooks';
import {
  HeroMonday,
  TrustStripMonday,
  InteractiveVideoDemoSection,
  DepartmentWorkOSMonday,
  AgentFleetMonday,
  WorkflowMonday,
  ROICalculatorMonday,
  TestimonialsMonday,
  CtaMonday,
} from '@/components/marketing/home';

/**
 * Rebuilt monday.com-grade marketing homepage for Rooflyzer OS.
 * Features crisp white canvas, authentic Rogue Move Agency purple (#7950A2) and warm honey gold (#F7C76C),
 * real photorealistic agent persona avatars, and zero AI-slop monospace telemetry clutter.
 */
export function HomePage() {
  const { data: cmsData } = useSanityPage('home');

  const pageTitle =
    cmsData?.seo?.metaTitle || 'Rooflyzer OS · AI Work OS for Modern Roofing Contractors';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    cmsData?.description ||
    'Rooflyzer OS is the autonomous AI work OS for roofers: 24/7 call answering, instant satellite takeoffs, storm dispatch, and milestone escrow invoicing.';

  const heroTitle = cmsData?.title || 'AI workspace for roofing contractors and their agents';
  const heroSubtitle =
    cmsData?.description ||
    'Autonomous AI agents answer homeowner calls 24/7, measure satellite roof pitch in 90 seconds, and collect milestone escrow draws. Built for roofing companies ready to scale.';
  const heroCta = 'Start Free Trial';
  const heroCtaLink = '/register';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Rooflyzer OS',
          url: 'https://www.rooflyzer.pro',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.rooflyzer.pro/?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />

      {/* 1. Monday.com-grade Hero with multi-agent portrait card stack */}
      <HeroMonday
        title={heroTitle}
        subtitle={heroSubtitle}
        ctaText={heroCta}
        ctaLink={heroCtaLink}
      />

      {/* 2. Trade & integration credibility bar */}
      <TrustStripMonday />

      {/* 3. Interactive Remotion + Recordly Auto-Zoom Video Demo Studio */}
      <InteractiveVideoDemoSection />

      {/* 4. Monday.com Department Work OS Board */}
      <div id="how-it-connects" className="scroll-mt-24">
        <DepartmentWorkOSMonday />
      </div>

      {/* 4. Meet Your 24/7 AI Fleet (Representative Agent Personas) */}
      <AgentFleetMonday />

      {/* 5. Autonomous Workflows / How it Connects */}
      <WorkflowMonday />

      {/* 6. Interactive ROI & Office Savings Calculator */}
      <ROICalculatorMonday />

      {/* 7. Field Scenarios & Contractor Social Proof */}
      <TestimonialsMonday />

      {/* 8. Final High-Conversion CTA */}
      <CtaMonday />
    </>
  );
}
